import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

const STEEL = 0x444a52;
const WOOD  = 0x6b4a2b;
const DARK  = 0x222428;
const BLADE = 0xc7ccd4;

// One material for every weapon, every character — saves materials and helps batching.
const SHARED_MATERIAL = new THREE.MeshLambertMaterial({ vertexColors: true });
const GEOMETRY_CACHE = new Map<string, THREE.BufferGeometry>();

function colorize(g: THREE.BufferGeometry, hex: number): THREE.BufferGeometry {
  const r = ((hex >> 16) & 255) / 255;
  const gn = ((hex >>  8) & 255) / 255;
  const bl =  (hex        & 255) / 255;
  const n = g.attributes.position.count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { arr[i*3] = r; arr[i*3+1] = gn; arr[i*3+2] = bl; }
  g.setAttribute("color", new THREE.BufferAttribute(arr, 3));
  return g;
}

function box(w: number, h: number, d: number, x: number, y: number, z: number, color: number): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  g.translate(x, y, z);
  return colorize(g, color);
}

function cyl(r: number, h: number, x: number, y: number, z: number, color: number, rotateX = 0): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, h, 8);
  if (rotateX) g.rotateX(rotateX);
  g.translate(x, y, z);
  return colorize(g, color);
}

function build(name: string): THREE.BufferGeometry {
  let parts: THREE.BufferGeometry[];
  switch (name) {
    case "Rifle":   parts = rifle(); break;
    case "SMG":     parts = smg(); break;
    case "Bolt":    parts = bolt(); break;
    case "Shotgun": parts = shotgun(); break;
    case "Knife":   parts = knife(); break;
    case "Hatchet": parts = hatchet(); break;
    case "Bat":     parts = bat(); break;
    default:        parts = rifle();
  }
  const merged = mergeGeometries(parts, false)!;
  for (const p of parts) p.dispose();
  return merged;
}

/** All weapons face -Z. Returns a Mesh that shares geometry + material across players. */
export function buildWeapon(name: string): THREE.Mesh {
  let g = GEOMETRY_CACHE.get(name);
  if (!g) { g = build(name); GEOMETRY_CACHE.set(name, g); }
  return new THREE.Mesh(g, SHARED_MATERIAL);
}

function rifle()   { return [
  box(0.08, 0.12, 0.32, 0, 0, 0, STEEL),
  box(0.045, 0.045, 0.6, 0, 0, -0.45, DARK),
  box(0.06, 0.1, 0.22, 0, 0, 0.25, WOOD),
  box(0.05, 0.13, 0.05, 0, -0.1, 0.06, DARK),
]; }

function smg() { return [
  box(0.08, 0.14, 0.22, 0, 0, 0, DARK),
  box(0.04, 0.04, 0.22, 0, 0, -0.22, STEEL),
  box(0.06, 0.14, 0.05, 0, -0.13, 0, STEEL),
  box(0.05, 0.1, 0.05, 0, -0.1, 0.08, DARK),
]; }

function bolt() { return [
  box(0.08, 0.1, 0.3, 0, 0, 0, STEEL),
  box(0.04, 0.04, 1.0, 0, 0, -0.6, DARK),
  box(0.07, 0.1, 0.32, 0, 0, 0.3, WOOD),
  cyl(0.05, 0.18, 0, 0.1, -0.05, DARK, Math.PI / 2),
  box(0.05, 0.12, 0.05, 0, -0.1, 0.08, DARK),
]; }

function shotgun() { return [
  box(0.1, 0.14, 0.3, 0, 0, 0, STEEL),
  box(0.04, 0.04, 0.55, -0.025, 0.025, -0.4, DARK),
  box(0.04, 0.04, 0.55,  0.025, 0.025, -0.4, DARK),
  box(0.07, 0.1, 0.24, 0, 0, 0.26, WOOD),
]; }

function knife() { return [
  box(0.02, 0.03, 0.22, 0, 0, -0.11, BLADE),
  box(0.06, 0.04, 0.02, 0, 0, 0.01, DARK),
  box(0.035, 0.04, 0.12, 0, 0, 0.08, WOOD),
]; }

function hatchet() { return [
  box(0.04, 0.04, 0.42, 0, 0, 0, WOOD),
  box(0.12, 0.1, 0.04, 0.06, 0, -0.22, STEEL),
  box(0.02, 0.12, 0.06, 0.12, 0, -0.22, BLADE),
]; }

function bat() { return [
  cyl(0.025, 0.25, 0, 0, 0.12, WOOD, Math.PI / 2),
  cyl(0.05, 0.4, 0, 0, -0.2, WOOD, Math.PI / 2),
]; }
